'use client';

import { useTranslation } from '@/hooks/useTranslation';
import { CycleStatus } from '@/services/api/performance';

/**
 * 周期状态
 */
interface CycleStatusBadgeProps {
  /** 状态 */
  status: CycleStatus;
  /** 尺寸 */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * 状态配置
 */
const STATUS_CONFIG: Record<CycleStatus, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-600' },
  GOAL_SETTING: { bg: 'bg-blue-100', text: 'text-blue-700' },
  IN_PROGRESS: { bg: 'bg-green-100', text: 'text-green-700' },
  EVALUATING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  CALIBRATING: { bg: 'bg-orange-100', text: 'text-orange-700' },
  CONFIRMING: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  COMPLETED: { bg: 'bg-purple-100', text: 'text-purple-700' },
  ARCHIVED: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

/**
 * 尺寸配置
 */
const SIZE_CLASSES = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-sm',
  lg: 'px-3 py-1 text-base',
};

/**
 * 周期状态徽章组件
 *
 * 用于展示绩效周期的当前状态，支持 7 个阶段：
 * - DRAFT: 草稿
 * - GOAL_SETTING: 目标设定中
 * - IN_PROGRESS: 执行中
 * - EVALUATING: 评估中
 * - CALIBRATING: 校准中
 * - COMPLETED: 已完成
 * - ARCHIVED: 已归档
 */
export function CycleStatusBadge({ status, size = 'md' }: CycleStatusBadgeProps) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[status];

  return (
    <span
      className={`inline-flex items-center rounded font-medium ${config.bg} ${config.text} ${SIZE_CLASSES[size]}`}
    >
      {t.performance?.cycle?.statuses?.[status]}
    </span>
  );
}
